import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Spinner } from 'react-bootstrap';
import { Search } from 'components';
import placeholderImage from 'assets/img/covers/placeholder.jpg';
import { useGetRegionPageAvailableLooks } from 'api/regions/hooks';
import { useSelectedProfileStore } from 'stores/selectedProfileStore';
import { useFetchRetailerPageAvailableLooks, useFetchStore } from 'api/store/hooks';
import { RETAILER } from 'constants/roles';
import { useAuthStore } from 'stores/authStore';
import * as Ui from './SelectLooksModal.styles';

const SelectLooksModal = ({ isOpen, onClose, onConfirm, initialSelected = [] }) => {
  const { t } = useTranslation('pages', { keyPrefix: 'widgets' });
  const { t: tApp } = useTranslation('app');

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(initialSelected);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelected(initialSelected);
    }
  }, [initialSelected, isOpen]);

  const isRetailer = useAuthStore((state) => state.role) === RETAILER;
  const regionUid = useSelectedProfileStore((state) => state.selectedRegionUid);
  const { data: store } = useFetchStore({ enabled: isRetailer });
  const { data: regionLooks = [], isLoading: isRegionLoading } = useGetRegionPageAvailableLooks(
    isRetailer ? undefined : regionUid,
  );
  const { data: storeLooks = [], isLoading: isStoreLoading } = useFetchRetailerPageAvailableLooks(
    store?.uid,
    { enabled: isRetailer && !!store?.uid },
  );
  const looks = isRetailer ? storeLooks : regionLooks;
  const isLoading = isRetailer ? isStoreLoading : isRegionLoading;

  const selectedUids = useMemo(() => new Set(selected.map((l) => l.uid)), [selected]);

  const filteredLooks = useMemo(() => {
    let list = looks;

    if (showSelectedOnly) {
      list = list.filter((l) => selectedUids.has(l.uid));
    }

    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter((l) => {
        const name = (l.name || '').toLowerCase();
        return name.includes(query);
      });
    }

    return list;
  }, [looks, search, showSelectedOnly, selectedUids]);

  const handleToggle = (look) => {
    if (selectedUids.has(look.uid)) {
      setSelected((prev) => prev.filter((l) => l.uid !== look.uid));
    } else {
      setSelected((prev) => [...prev, look]);
    }
  };

  const handleConfirm = () => {
    onConfirm(selected);
    onClose();
  };

  const handleClose = () => {
    setSelected(initialSelected);
    setShowSelectedOnly(false);
    onClose();
  };

  return (
    <Ui.StyledModal
      open={isOpen}
      header={{ title: t('selectLooks') }}
      closeOnDocumentClick
      onClose={handleClose}
      bodyStyle={{
        display: 'flex',
        flexDirection: 'column',
        overflowY: 'hidden',
        paddingBottom: 0,
      }}
      footer={() => (
        <Ui.Footer>
          <Button variant="white" onClick={handleClose} className="rounded">
            {tApp('cancel')}
          </Button>
          <Button
            variant="primary"
            className="rounded"
            disabled={selected.length === 0}
            onClick={handleConfirm}
          >
            {tApp('confirm')} ({selected.length})
          </Button>
        </Ui.Footer>
      )}
    >
      <Ui.ModalBody>
        <Ui.Toolbar>
          <Ui.SearchWrapper>
            <Search value={search} onChange={setSearch} placeholder={t('searchLooksPlaceholder')} />
          </Ui.SearchWrapper>
          <Ui.FilterButton
            type="button"
            $active={showSelectedOnly}
            onClick={() => setShowSelectedOnly((v) => !v)}
          >
            {t('selectedLooksCount', { count: selected.length })}
          </Ui.FilterButton>
        </Ui.Toolbar>
        <Ui.ScrollableWrapper>
          <Ui.LooksGrid>
            {isLoading ? (
              <Ui.EmptyState>
                <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              </Ui.EmptyState>
            ) : filteredLooks.length === 0 ? (
              <Ui.EmptyState>{t('noLooksFound')}</Ui.EmptyState>
            ) : (
              filteredLooks.map((look) => {
                const isSelected = selectedUids.has(look.uid);
                return (
                  <Ui.LookCard
                    key={look.uid}
                    $selected={isSelected}
                    onClick={() => handleToggle(look)}
                  >
                    <Ui.LookImage
                      src={look.media_thumbnails[0] || placeholderImage}
                      alt={look.name}
                    />
                    <Ui.LookInfo>{look.name && <Ui.LookName>{look.name}</Ui.LookName>}</Ui.LookInfo>
                  </Ui.LookCard>
                );
              })
            )}
          </Ui.LooksGrid>
        </Ui.ScrollableWrapper>
      </Ui.ModalBody>
    </Ui.StyledModal>
  );
};

export default SelectLooksModal;
